import {
  simulateReadableStream,
  type ChatTransport,
  type UIMessageChunk,
} from "ai";
import type { LumeUIMessage } from "./types";

/**
 * The stub reply for the onboarding thread, where the widget above the composer
 * is the real way forward.
 */
export const ONBOARDING_MOCK_REPLY = [
  "Ainda não estou ligado a um modelo — este chat é só a casca por enquanto. ",
  "Mas os botões acima já funcionam: escolha como quer montar o seu caderno ",
  "e eu levo você para o passo seguinte.",
];

/**
 * A conversation with no agent behind it, for a surface that does not have one yet.
 *
 * Only the onboarding thread uses this now. The caderno drawer talks to the real
 * agent (agent/mastra-transport.ts) and has NO stub behind it on purpose: a canned
 * line in place of a failed request reads like the tutor answered and had nothing to
 * say, which is worse than an error the student can act on.
 *
 * It implements the same ChatTransport interface, so the whole client pipeline —
 * sendMessage, streaming chunks, status transitions, persistence — is exercised for
 * real on a surface whose agent is still to be written.
 */
export class NotConnectedChatTransport
  implements ChatTransport<LumeUIMessage>
{
  private readonly reply: string[];

  constructor(reply: string[] = ONBOARDING_MOCK_REPLY) {
    this.reply = reply;
  }

  async sendMessages(): Promise<ReadableStream<UIMessageChunk>> {
    const textId = "not-connected-text";

    return simulateReadableStream<UIMessageChunk>({
      chunkDelayInMs: 120,
      chunks: [
        { type: "start" },
        { type: "start-step" },
        { type: "text-start", id: textId },
        ...this.reply.map((delta) => ({
          type: "text-delta" as const,
          delta,
          id: textId,
        })),
        { type: "text-end", id: textId },
        { type: "finish-step" },
        { type: "finish" },
      ],
      initialDelayInMs: 400,
    });
  }

  // No server-side stream to resume, so there is never anything to reconnect to
  async reconnectToStream(): Promise<ReadableStream<UIMessageChunk> | null> {
    return null;
  }
}
